import {
  closest,
  toArray,
  toggleAttribute,
  testReducedMotion,
  withinRange,
  setAttribute
} from '@nrk/dh-basis-common'
import { withinStops } from '../dh-basis-common/lib/frontend/range'

const STOPS_ATTR = 'stops'
const TARGET_STOP_ATTR = 'target-stop'
const AUTOPLAY_BLOCKED = 'autoplay-blocked'
const STATE_ATTR = 'data-state' // Data attribute, so that it can be used on other elements
const VIEWPORT_POSITION_ATTR = 'data-viewport-position'

// Most videos have a frame-rate of 25 fps, but not all. Use with care.
// Used to calculate target zone.
const FRAME_RATE = 25

const isVisible = child => child.offsetWidth > 0 || child.offsetHeight > 0

/* Ponyfill Math.sign */
function sign(val) {
  return val >= 0 ? 1 : -1
}

function round(num, decimals) {
  const multiplier = Math.pow(10.0, decimals)
  return Math.round(num * multiplier) / multiplier
}

export default class StegvisVideo extends HTMLElement {
  static get observedAttributes() {
    return [TARGET_STOP_ATTR, STATE_ATTR, VIEWPORT_POSITION_ATTR]
  }

  _paused = true

  _disconnecting = false

  _reducedMotionSeekEnabled = false

  connectedCallback() {
    this.addEventListener('click', this._handleClick)
    this.videoElements.forEach(videoElement =>
      videoElement.addEventListener('loadedmetadata', this._onReadyStateChange)
    )
  }

  disconnectedCallback() {
    this._disconnecting = true
    this.removeEventListener('click', this._handleClick)
    this.videoElements.forEach(videoElement =>
      videoElement.removeEventListener('loadedmetadata', this._onReadyStateChange)
    )
  }

  attributeChangedCallback(name, prevValue, newValue) {
    const hasValue = newValue !== null
    const valueChanged = prevValue !== newValue
    if (!valueChanged) {
      return
    }

    if (name === STATE_ATTR) {
      this._stateChanged(prevValue, newValue)
    } else if (name === VIEWPORT_POSITION_ATTR) {
      this._viewportChanged(newValue)
    } else if (name === TARGET_STOP_ATTR) {
      hasValue && this._startSeek()
    }
  }

  _onReadyStateChange = e => {
    if (isVisible(e.target) && e.target.readyState >= 1) {
      this._startSeek()
    }
  }

  _stateChanged(prevState, nextState) {
    if (nextState === 'ACTIVE') {
      if (prevState === 'NEXT') {
        this._gotoStart()
      } else if (prevState === 'PREV') {
        this._gotoEnd()
      }
      this._startSeek()
    } else {
      this._paused = true
    }
  }

  _viewportChanged(newValue) {
    if (newValue === 'IN-VIEWPORT') {
      this._preload()
      this._startSeek()
    } else if (newValue === 'ABOVE-VIEWPORT') {
      this._paused = true
      this._gotoEnd()
    } else if (newValue === 'BELOW-VIEWPORT') {
      this._paused = true
      this._gotoStart()
    }
  }

  _handleClick = e => {
    const btn = closest(e.target, 'button')
    if (!btn) {
      return
    }

    /* Triggers play if autoplay was blocked */
    const videoElement = this.videoElement
    if (videoElement.paused) {
      const playPromise = videoElement.play() || Promise.resolve()
      playPromise.then(() => {
        this.autoplayBlocked = false
      })
    }
  }

  _shouldPlay() {
    const inViewport =
      typeof this.viewportPosition === 'undefined' || this.viewportPosition === 'IN-VIEWPORT'
    return this.videoElement.readyState >= 1 && inViewport && this.state === 'ACTIVE'
  }

  _preload() {
    if (this.videoElement.getAttribute('preload') !== 'auto') {
      this.videoElement.setAttribute('preload', 'auto')
    }
  }

  _gotoStart() {
    this.videoElement.currentTime = 0
  }

  _gotoEnd() {
    this.videoElement.currentTime = this.videoElement.duration
  }

  _enableSeekOnReducedMotion() {
    if (this._reducedMotionSeekEnabled) {
      return Promise.resolve()
    }

    const videoElement = this.videoElement
    return (videoElement.play() || Promise.resolve(true))
      .then(() => {
        videoElement.pause()
        this._reducedMotionSeekEnabled = true
      })
      .catch(e => {
        // Ignore error
      })
  }

  _startSeek() {
    if (!this._paused || !this._shouldPlay()) {
      return
    }

    const reducedMotion = testReducedMotion()
    this._paused = false

    let prevTime
    const loop = timestamp => {
      const videoElement = this.videoElement
      if (this._disconnecting || this._paused || !videoElement) {
        this._paused = true
        this.videoElement.pause()
        return
      }

      if (!prevTime) {
        prevTime = timestamp
      }

      this.invisibleVideoElements
        .filter(video => !video.paused)
        .forEach(invisibleVideo => invisibleVideo.pause())

      const targetTime = this.stops[this.targetStop]

      const dt = (timestamp - prevTime) / 1000
      const framesPerTick = Math.max(Math.ceil(Math.abs(dt * FRAME_RATE)), 1)
      const buffer = (0.5 * framesPerTick) / FRAME_RATE

      const currentTime = videoElement.currentTime
      const atTargetStop = withinRange(currentTime, targetTime - buffer, targetTime + buffer)

      if (atTargetStop) {
        videoElement.currentTime = targetTime
        this._paused = true
        videoElement.pause()
      } else if (reducedMotion) {
        this._enableSeekOnReducedMotion().then(() => {
          videoElement.currentTime = targetTime
          this._paused = true
        })
      } else {
        const speed = this._calculateSpeed(currentTime)
        const direction = sign(targetTime - currentTime)

        if (direction === 1) {
          // Use normal playback for forward playback
          videoElement.playbackRate = speed
          if (videoElement.paused && !this.autoplayBlocked) {
            const playPromise = videoElement.play() || Promise.resolve()
            playPromise.catch(e => {
              console.error(e)
              this.autoplayBlocked = true
            })
          }
        } else {
          // Use seeking for reverse playback:
          // https://github.com/whatwg/html/issues/3754
          videoElement.pause()
          const nextTime = round(currentTime + dt * speed * direction, 2.0)
          if (currentTime !== nextTime) {
            videoElement.currentTime = nextTime
          }
        }
        prevTime = timestamp
        requestAnimationFrame(loop)
      }
    }
    requestAnimationFrame(loop)
  }

  _calculateSpeed(currentTime) {
    return withinStops(currentTime, this.stops, this.targetStop, 1) ? 1 : 3
  }

  get videoElement() {
    return this.videoElements.find(isVisible)
  }

  get invisibleVideoElements() {
    return this.videoElements.filter(child => !isVisible(child))
  }

  get videoElements() {
    return toArray(this.children).filter(child => child.nodeName === 'VIDEO')
  }

  get stops() {
    const stopString = this.getAttribute(STOPS_ATTR) || ''
    return stopString
      .replace(/[[\]]/g, '')
      .split(',')
      .map(s => s.trim())
      .filter(Boolean)
      .map(s => Number(s))
  }

  set stops(val) {
    this.setAttribute(STOPS_ATTR, val.map(num => num.toFixed(2)).join(','))
  }

  get targetStop() {
    return Number(this.getAttribute(TARGET_STOP_ATTR) || 0)
  }

  set targetStop(val) {
    this.setAttribute(TARGET_STOP_ATTR, val)
  }

  get state() {
    return this.getAttribute(STATE_ATTR)
  }

  set state(val) {
    setAttribute(this, STATE_ATTR, val)
  }

  get viewportPosition() {
    return this.getAttribute(VIEWPORT_POSITION_ATTR)
  }

  set viewportPosition(val) {
    this.setAttribute(VIEWPORT_POSITION_ATTR, val)
  }

  get autoplayBlocked() {
    return this.hasAttribute(AUTOPLAY_BLOCKED) || false
  }

  set autoplayBlocked(val) {
    toggleAttribute(this, AUTOPLAY_BLOCKED, val)
  }
}
